
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useSearch } from '@tanstack/react-router'
import GenieLogo from '@/components/ui/icon/icons/GenieLogo'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Icon } from '@/components/ui/icon'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Link } from '@tanstack/react-router'
import { resetPassword } from '@/lib/auth-client'
import { safeAuthRedirect } from '@/lib/auth-redirect'
import { z } from 'zod'

// Better Auth compatible password reset schema
const PasswordResetSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string()
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword']
})

type PasswordResetFormData = z.infer<typeof PasswordResetSchema>

export function ResetPassword() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { token?: string; redirect?: string; error?: string }
  const redirect = safeAuthRedirect(search.redirect)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [token, setToken] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordResetFormData>({
    resolver: zodResolver(PasswordResetSchema),
  })

  // Combined loading state for all UI elements
  const isFormDisabled = isSubmitting || isLoading

  // Extract token from URL on component mount
  useEffect(() => {
    const urlToken = search.token
    if (urlToken) {
      setToken(urlToken)
    } else {
      setError('This reset link is invalid or expired. Request a new link below.')
    }
  }, [search.token])

  const onSubmit = async (data: PasswordResetFormData) => {
    if (!token) {
      setError('Reset token is required')
      return
    }

    setError("")
    setIsLoading(true)

    try {
      const result = await resetPassword({
        token: token,
        newPassword: data.password
      })
      if (!result.error && result.data) {
        // Password reset successful, redirect to login
        await navigate({ to: '/login', search: { redirect } })
      } else {
        setError(result.error?.message || 'Password reset failed. Request a new link and try again.')
      }
    } catch (error) {
      setError("An unexpected error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="md:bg-muted flex items-center justify-center bg-cover md:min-h-screen">
      <div className="flex h-screen w-full flex-col md:h-auto md:max-w-[440px] md:px-4 md:py-6">
        <Card className="rounded-none p-6 md:rounded-xl">
          <CardHeader className="mb-6 flex flex-col items-start border-b-0 p-0 text-left">
            <GenieLogo className="mb-6 h-12 w-12" />
            <CardTitle className="text-lg font-bold">Reset password</CardTitle>
            <CardDescription>Choose a new password for your account.</CardDescription>
          </CardHeader>

          <CardContent className="p-0">
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Error Display */}
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                  <Link to="/forgot-password" search={{ redirect }} className="mt-2 block underline">Request a new reset link</Link>
                </Alert>
              )}

              {/* Reset Password Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      placeholder="Enter your new password"
                      type={showPassword ? 'text' : 'password'}
                      className="pr-8"
                      disabled={isFormDisabled}
                      {...register('password')}
                    />
                    <button
                      type="button"
                      className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground"
                      disabled={isFormDisabled}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <Icon name={showPassword ? 'eye-off' : 'eye'} size="sm" />
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-destructive">
                      {errors.password.message}
                    </p>
                  )}
                  <p className="text-muted-foreground text-sm">
                    Minimum 8 characters.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password-repeat">Confirm New Password</Label>
                  <Input
                    id="password-repeat"
                    placeholder="Confirm your new password"
                    type={showPassword ? 'text' : 'password'}
                    disabled={isFormDisabled}
                    {...register('confirmPassword')}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-destructive">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                <Button size="lg" className="w-full" type="submit" disabled={isFormDisabled}>
                  {isFormDisabled && (
                    <svg
                      className="mr-2 h-4 w-4 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                  Update password
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
